import { useEffect, useMemo, useRef, useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parse,
  parseISO,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from './utils';

/**
 * DateField — date input with floating label, an inline typed input
 * (mask `GG/MM/AAAA`) and a calendar popover.
 *
 * Two interaction modes:
 *   1. Typing — user writes digits in the input; `/` separators are
 *      inserted automatically after positions 2 and 4. The component
 *      emits `onChange` with the ISO `YYYY-MM-DD` string only when the
 *      typed value is a fully-formed valid date (8 digits, real
 *      day/month). On blur or Enter with an invalid/partial value the
 *      input reverts to the last valid `value` prop (no save).
 *   2. Calendar — click the calendar icon to open the popover; picking
 *      a day fires `onChange` and updates the typed input in lock-step.
 *
 * Why not `<input type="date">`:
 *   The native date input renders an OS-themed picker (different on
 *   Chrome/Firefox/Safari/Windows/macOS) that breaks the visual
 *   consistency of the backoffice forms. This primitive is the
 *   in-house replacement that mirrors the style used elsewhere in the
 *   app: rounded-md trigger with floating label, dropdown popover
 *   anchored under the trigger, Italian month + day-of-week labels.
 *
 * Visual model (matches an external design system pattern shared by
 * the operator — described in concrete parameters below; the source
 * is intentionally not named in this file):
 *   - Trigger shell: 48px tall, rounded-md, bg-foreground/5, label up
 *     top (xs muted), typed input on the line below (sm foreground),
 *     calendar icon button on the right; when a value is set an `X`
 *     clear button appears next to the calendar icon.
 *   - Popover: rounded-md, padded p-3, shadow-glass + backdrop-blur,
 *     same Pattern B surface as `SelectField`.
 *   - Header: month + year in `it-IT`, chevron prev/next.
 *   - Day-of-week row: L M M G V S D (Mon-first per Italian convention).
 *   - Day grid: 6 weeks × 7 days. Adjacent-month cells visible but
 *     muted. Selected day = filled accent circle.
 *
 * Interface mirrors `<input type="date">`: ISO `YYYY-MM-DD` strings on
 * `value` / `onChange`. `''` (empty string) = no value. Callers DON'T
 * deal with `Date` objects at all.
 *
 * Implementation note — Radix primitive:
 *   Uses `DropdownMenuPrimitive` (already in the bundle) instead of
 *   adding `@radix-ui/react-popover` as a new dep. The calendar grid
 *   buttons are plain `<button>`s (not `DropdownMenuItem`) so Radix
 *   doesn't impose menu-style arrow-key navigation between cells.
 *   Click-outside + ESC + focus trap come for free from the menu root.
 */

export interface DateFieldProps {
  /** Floating-label text. */
  label: string;
  /** ISO 8601 date string (`YYYY-MM-DD`). Empty string = no value. */
  value: string;
  /** Called with the new value on selection, or `''` on clear. */
  onChange: (next: string) => void;
  /** Disable the trigger. */
  disabled?: boolean;
  /** Forwarded to the trigger button. */
  className?: string;
  /**
   * Optional placeholder shown in the typed input when no date is set.
   * Defaults to `'GG/MM/AAAA'` to hint at the input mask.
   */
  placeholder?: string;
  /**
   * When `true`, the X clear button is hidden even if a value is set.
   * Use this for required fields where "no date" is not a valid state.
   */
  hideClear?: boolean;
}

const DAY_LABELS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'] as const;

// Plausible year range for typed dates. Outside this band the typed
// value is rejected even if dd/mm parse succeeds — guards against typos
// like "0025" → 25 AD. Calendar pick is never affected (popover only
// emits real selected days).
const MIN_YEAR = 1900;
const MAX_YEAR = 2200;

function parseValue(value: string): Date | null {
  if (!value) return null;
  const d = parseISO(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function toIsoDateString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function buildCalendarGrid(viewMonth: Date): Date[] {
  const firstOfMonth = startOfMonth(viewMonth);
  const gridStart = startOfWeek(firstOfMonth, { weekStartsOn: 1 });
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

/**
 * Format raw input into the GG/MM/AAAA mask.
 * - Strips non-digits (so the user can paste `12-03-2025` or
 *   `12.03.2025` and it normalises).
 * - Caps at 8 digits.
 * - Inserts `/` after the day (pos 2) and the month (pos 4).
 *
 * Pure transformation — does NOT validate that the date is real.
 * Validation happens at parse time before emitting onChange.
 */
function formatTyped(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Convert ISO `YYYY-MM-DD` to typed-mask `GG/MM/AAAA`. Returns `''` for
 * empty / invalid ISO. Used to keep the typed input in sync with the
 * `value` prop on external updates and on calendar selection.
 */
function isoToTyped(value: string): string {
  const d = parseValue(value);
  if (!d) return '';
  return format(d, 'dd/MM/yyyy');
}

/**
 * Try to parse a fully-formed `GG/MM/AAAA` typed value into an ISO
 * date string. Returns `null` when:
 * - the input doesn't have 8 digits (incomplete)
 * - the date isn't a real calendar day (e.g. 31/02)
 * - the year is outside the plausible band
 */
function typedToIso(typed: string): string | null {
  const digits = typed.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const parsed = parse(typed, 'dd/MM/yyyy', new Date());
  if (!isValid(parsed)) return null;
  // Round-trip guard: date-fns silently overflows impossible days
  // (e.g. 31/02 → 03/03). Reject when re-formatting the parsed date
  // doesn't yield the original typed string.
  if (format(parsed, 'dd/MM/yyyy') !== typed) return null;
  const year = parsed.getFullYear();
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  return toIsoDateString(parsed);
}

export function DateField({
  label,
  value,
  onChange,
  disabled,
  className,
  placeholder,
  hideClear,
}: DateFieldProps): JSX.Element {
  const selectedDate = useMemo(() => parseValue(value), [value]);
  const today = useMemo(() => startOfToday(), []);
  const [viewMonth, setViewMonth] = useState<Date>(
    () => selectedDate ?? today,
  );
  const [open, setOpen] = useState(false);
  // Typed mirror of `value`. Stays a string so the user can type
  // partial dates without immediately invalidating; emits onChange to
  // the parent only when the parse succeeds (or on clear).
  const [typedValue, setTypedValue] = useState<string>(() => isoToTyped(value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the typed input in sync with the prop. Avoid clobbering the
  // user's in-progress edits by skipping when the displayed value
  // already matches the prop's mask form.
  useEffect(() => {
    const next = isoToTyped(value);
    setTypedValue((prev) => (prev === next ? prev : next));
  }, [value]);

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      setViewMonth(selectedDate ?? today);
    }
    setOpen(next);
  };

  const handleClear = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setTypedValue('');
    onChange('');
  };

  const handleSelectDay = (date: Date): void => {
    const iso = toIsoDateString(date);
    setTypedValue(format(date, 'dd/MM/yyyy'));
    onChange(iso);
    setOpen(false);
  };

  const handleTypedChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = formatTyped(e.target.value);
    setTypedValue(next);
    // Optimistic emit when the typed value parses to a complete date.
    // Empty input → emit clear so the parent state mirrors the input.
    if (next.length === 0 && value !== '') {
      onChange('');
      return;
    }
    const iso = typedToIso(next);
    if (iso !== null && iso !== value) {
      onChange(iso);
    }
  };

  const handleTypedBlur = (): void => {
    // Empty after blur → ensure parent is cleared too.
    if (typedValue.length === 0) {
      if (value !== '') onChange('');
      return;
    }
    const iso = typedToIso(typedValue);
    if (iso === null) {
      // Invalid / partial — revert visual to the last valid prop value.
      setTypedValue(isoToTyped(value));
      return;
    }
    if (iso !== value) onChange(iso);
  };

  const handleTypedKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTypedBlur();
      inputRef.current?.blur();
    }
  };

  const cells = useMemo(() => buildCalendarGrid(viewMonth), [viewMonth]);
  const monthLabel = format(viewMonth, 'MMMM yyyy', { locale: it });

  const showClear = !hideClear && typedValue.length > 0 && !disabled;

  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div
        className={cn(
          // `min-w-0` is the critical bit when DateField sits inside a
          // grid/flex parent with auto-min sizing (the default for
          // `grid-cols-N`). Without it, the row's intrinsic min-content
          // grows when the X clear button mounts (h-5 w-5 + gap), the
          // grid track expands to fit, and sibling cells get pushed out
          // of line. With `min-w-0` the field shrinks to its allotted
          // track regardless of the trailing button cluster.
          'flex h-control w-full min-w-0 items-center gap-2 rounded-md bg-foreground/5 px-3 transition-colors hover:bg-foreground/10 focus-within:bg-foreground/10',
          disabled ? 'cursor-not-allowed opacity-50' : '',
          className,
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <label
            className="text-xs text-muted-foreground"
            htmlFor={undefined}
          >
            {label}
          </label>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label={label}
            disabled={disabled}
            value={typedValue}
            onChange={handleTypedChange}
            onBlur={handleTypedBlur}
            onKeyDown={handleTypedKeyDown}
            placeholder={placeholder ?? 'GG/MM/AAAA'}
            // Cap a touch above 10 to be defensive vs IME / paste edge
            // cases; formatTyped clamps digits to 8 anyway.
            maxLength={12}
            // `w-full min-w-0` so the input fills its flex column and
            // can shrink below its `size` default — without it the
            // input's intrinsic width pushes the parent flex/grid track
            // outwards when the X clear button or calendar icon are
            // present. Pairs with the `min-w-0` on the root container.
            className="w-full min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
          />
        </div>
        {showClear ? (
          // Span (not nested button — a button inside the popover
          // trigger button would be invalid HTML; keeping it as an
          // accessible role="button" stays consistent with the
          // CountryPicker / SelectField pattern).
          <span
            role="button"
            tabIndex={0}
            aria-label="Cancella data"
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setTypedValue('');
                onChange('');
              }
            }}
            className="flex h-5 w-5 flex-none items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </span>
        ) : null}
        <DropdownMenuPrimitive.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Apri calendario"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground focus-visible:bg-foreground/10 focus-visible:outline-none disabled:cursor-not-allowed"
          >
            <CalendarDays className="h-4 w-4" aria-hidden />
          </button>
        </DropdownMenuPrimitive.Trigger>
      </div>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={4}
          // Same surface treatment as SelectField (Pattern B).
          data-select-field-content
          className="z-popover rounded-md p-3 text-app-text shadow-glass backdrop-blur-2xl"
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Header — month label + prev/next chevrons */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="Mese precedente"
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground focus-visible:bg-foreground/10 focus-visible:outline-none"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <span className="text-sm font-medium capitalize text-foreground">
              {monthLabel}
            </span>
            <button
              type="button"
              aria-label="Mese successivo"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground focus-visible:bg-foreground/10 focus-visible:outline-none"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {/* Day-of-week row */}
          <div className="grid grid-cols-7 gap-y-1">
            {DAY_LABELS.map((d, idx) => (
              <span
                key={`dow-${idx}`}
                className="flex h-7 w-9 items-center justify-center text-xs font-medium text-muted-foreground"
              >
                {d}
              </span>
            ))}
          </div>

          {/* Day grid — 6 weeks × 7 days */}
          <div className="grid grid-cols-7 gap-y-1">
            {cells.map((cell, idx) => {
              const inMonth = isSameMonth(cell, viewMonth);
              const isSelected = selectedDate
                ? isSameDay(cell, selectedDate)
                : false;
              const isToday = isSameDay(cell, today);
              return (
                <button
                  key={`day-${idx}`}
                  type="button"
                  onClick={() => handleSelectDay(cell)}
                  aria-label={format(cell, "EEEE d MMMM yyyy", { locale: it })}
                  aria-pressed={isSelected}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full text-sm transition-colors focus-visible:outline-none',
                    isSelected
                      ? 'bg-accent font-semibold text-accent-foreground'
                      : inMonth
                        ? 'text-foreground hover:bg-foreground/10'
                        : 'text-muted-foreground hover:bg-foreground/10',
                    !isSelected && isToday ? 'ring-1 ring-foreground/20' : '',
                  )}
                >
                  {cell.getDate()}
                </button>
              );
            })}
          </div>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
